import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { CreditScore } from '../../types/buyerFinancialData';

interface CreditScoreChartProps {
  creditScores: CreditScore[];
  className?: string;
}

export function CreditScoreChart({ creditScores, className = '' }: CreditScoreChartProps) {
  // Transform data for chart
  const chartData = creditScores.map((score, index) => ({
    bureau: score.bureau,
    score: score.score,
    date: new Date(score.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
    fullDate: score.date
  }));

  // Calculate average score
  const averageScore = creditScores.reduce((sum, score) => sum + score.score, 0) / creditScores.length;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-600">
            Score: <span className="font-semibold">{payload[0].value}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Credit Score Analysis</h3>
        <p className="text-sm text-gray-600">
          Credit scores across all three major bureaus. SBA typically requires scores of 650 or higher.
        </p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="bureau" 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              domain={[600, 800]}
              tickFormatter={(value) => value.toString()}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* SBA minimum requirement line */}
            <ReferenceLine 
              y={650} 
              stroke="#ef4444" 
              strokeDasharray="5 5" 
              label={{ value: "SBA Minimum (650)", position: "topRight" }}
            />
            
            {/* Excellent credit line */}
            <ReferenceLine 
              y={750} 
              stroke="#10b981" 
              strokeDasharray="5 5" 
              label={{ value: "Excellent (750+)", position: "topRight" }}
            />
            
            <Line
              type="monotone"
              dataKey="score"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 6 }}
              activeDot={{ r: 8, stroke: '#3b82f6', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{Math.round(averageScore)}</div>
          <div className="text-sm text-gray-600">Average Score</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {Math.max(...creditScores.map(s => s.score))}
          </div>
          <div className="text-sm text-gray-600">Highest Score</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            {Math.min(...creditScores.map(s => s.score))}
          </div>
          <div className="text-sm text-gray-600">Lowest Score</div>
        </div>
      </div>
    </div>
  );
}

